import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { unitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Alert } from '@/components/Alert'
import { Pager } from '@/components/Pager'
import { GridTable } from '@/components/GridTable'
import { NoteThread } from '@/components/NoteThread'
import { MCancel } from '@/screens/m-cancel/MCancel'
import { useCursorPager } from '@/data/useCursorPager'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { formatJobTime, formatBytes, JOB_STATUS_CHIP } from '@/screens/shared/jobFormatting'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type BackupJob = components['schemas']['BackupJob']
type SnapshotRepository = components['schemas']['SnapshotRepository']
type SnapshotProject = components['schemas']['SnapshotProject']
type SnapshotWorkspaceHook = components['schemas']['SnapshotWorkspaceHook']
type LogPage = components['schemas']['LogPage']
type JobNote = components['schemas']['JobNote']

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pending',
  RUNNING: 'Running',
  SUCCEEDED: 'Succeeded',
  PARTIAL_SUCCESS: 'Partial success',
  FAILED: 'Failed',
  SKIPPED: 'Skipped',
  CANCELLED: 'Cancelled',
}

type Tab = 'repositories' | 'details' | 'notes' | 'log'

/**
 * `v-backupjob` — process/09-SCREEN-SPECS/v-backupjob.md. **The repositories tab reads
 * `jobListSnapshotRepositories` — the manifest — never `jobListBackupRepositories`** (the
 * in-flight progress list): the manifest holds one row per in-scope repository, changed or not,
 * while the progress list is sparse by design (an unchanged repository has no row there at all) —
 * confusing the two is the exact trap W109 exists to name. **`content_bytes`/`commit_count` are
 * null-vs-zero distinct** (rule 3): 0 means measured and unchanged, null is never rendered as 0.
 *
 * **The "Inherited" row this spec's rules describe has no corresponding served field found in
 * `SnapshotRepository.status`** (a plain `JobStatus`, seven ordinary members, no `UNCHANGED`
 * value) — rendered here as the job's own real status per row rather than an invented derivation.
 * Reported as an open question, not resolved by guessing.
 *
 * **`panel`/`panel-header`/`meta-block`/`kv-value`/`grid-tools`/`icon-button`/`tools` added W164** —
 * each tab's own content wrapped in a real `panel`, `kv-grid` renamed to the spec's own
 * `meta-block`, every value cell given `data-cmp="kv-value"` (was bare, no tag at all), and a real
 * "Refresh" `icon-button` added both per-tab (`grid-tools`, scoped to that tab's own query) and at
 * the tab bar itself (`tools`, matching the prototype's own placement — a page-level refresh next
 * to the tabs, not inside any one panel).
 *
 * **This spec's own `segmented-control`/`segment-option` row ("data backup versus configuration
 * backup") is a confirmed misattribution, not built.** That exact toggle exists in the prototype —
 * on `v-dashboard`'s own "Backup jobs history"/"Backup jobs summary" panels, not here — and
 * `VDashboard.tsx`'s own doc comment already reports it as a simplification not built this pass.
 * The prototype's real `v-backupjob` markup has a *different* segmented control instead ("All" /
 * "Errors only" on the Logs tab), but `jobGetBackupLogs` has no level-filter parameter at all
 * (only `cursor`/`page_size`) — a client-side filter would silently only search the one loaded
 * page, misrepresenting "no errors on this page" as "no errors in this job." Building either
 * version would be inventing scope this contract does not support; reported, not guessed.
 *
 * **`grid-filter-row` and `link` also confirmed not buildable, not merely unattempted.**
 * `jobListSnapshotRepositories` (the Repositories tab's own read) takes only `cursor`/`page_size` —
 * no filter parameter exists to back a filter row. No unit anywhere in the 116-unit register is a
 * per-repository detail screen, so a repository name here has no honest internal destination to
 * link to, and `SnapshotRepository` itself carries no URL field for an external one.
 *
 * **Both status chips now use `JOB_STATUS_CHIP` (W172)** — previously `variant={status === 'FAILED'
 * ? 'default' : 'plain'}`, which rendered a flat blue for `FAILED` and flat grey for every other
 * outcome including `SUCCEEDED`. `Chip.tsx` only had three variants until W172; the correct
 * ten-variant mapping is `src/screens/shared/jobFormatting.ts`'s own doc comment.
 *
 * **`notes` tab added (Sanket, for consistency with `v-restorejob`)** — `Prototype_v2.html`'s own
 * `#bj-tabs` draws only "Job details"/"Logs", no notes pane, but `JobNote`'s own doc comment states
 * one table backs notes for both job kinds, and `jobListBackupNotes`/`jobAddBackupNote` are real,
 * shipped operations — the prototype is behind the contract here, not the other way round. Shares
 * `NoteThread.tsx` with `v-restorejob` rather than a second copy.
 *
 * **First tab is now kind-aware (Sanket, 2026-09-25, found live)** — it used to render the same
 * git-shaped `SnapshotRepository` grid unconditionally, so a `CONFIG_BACKUP` job showed a
 * "Repositories" tab (columns: branches, commits, bytes) for a job that captures no git content at
 * all. A `CONFIG_BACKUP` job now shows "Configuration items" instead: `jobListSnapshotProjects` and
 * `jobListSnapshotWorkspaceHooks` — the two manifest-browsing reads that already existed but were
 * only ever wired into the restore wizard's picker panes, never here. Repository-level configuration
 * (branch restrictions, deploy keys, pipeline variables — `VBackup.tsx`'s own `repo-config` category)
 * and pipeline/deployment history have no per-item manifest read in this contract at all, so this
 * pane says so and links to `/backup` for the full capture list rather than inventing a browsable
 * list the API cannot back.
 *
 * **Log tab rebuilt from page-replacing to accumulating (Sanket, 2026-09-25, found live)** — "Paged
 * by cursor only" with a Prev/Next `Pager` meant every "Next" click threw away the lines already on
 * screen, which reads exactly like "the log only has half of what I expect" for anyone who pages
 * forward once. There is no `prev_cursor` in this contract (§24.2 rule 25) so going "back" to
 * re-see them meant losing whatever was *ahead*. Replaced with a running transcript plus a "Load
 * more" action that only ever appends — nothing already shown is ever removed from view. `page_size`
 * is a declared-but-dead parameter on both sides of this contract (the FastAPI handler doesn't
 * accept it) — reported to the backend owner, not worked around here, since a client-side hint the
 * server can't read wouldn't do anything.
 */
export function VBackupjob() {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()
  const pager = useCursorPager()
  const projectsPager = useCursorPager()
  const hooksPager = useCursorPager()
  const [tab, setTab] = useState<Tab>('repositories')
  const [cancelling, setCancelling] = useState(false)
  const [morePages, setMorePages] = useState<readonly LogPage[]>([])

  const job = useQuery({
    queryKey: ['backup-job', orgId, workspaceId, jobId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<BackupJob> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/backups/{job_id}', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as BackupJob
    },
  })

  const isConfigBackup = job.data?.backup_kind === 'CONFIG_BACKUP'

  const repositories = useQuery({
    queryKey: ['backup-job-repos', orgId, workspaceId, jobId, pager.cursor],
    // Waits on `job.data` too, not just `!isConfigBackup` — that flag is false-by-default until the
    // job loads, and this tab is the default one, so without the wait this fired once for every
    // CONFIG_BACKUP job too, in the gap before `backup_kind` was known (found while adding this
    // screen's own regression test, 2026-09-25).
    enabled: tab === 'repositories' && job.data !== undefined && !isConfigBackup,
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/snapshots/{job_id}/repositories', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! }, query: { cursor: pager.cursor } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const projects = useQuery({
    queryKey: ['backup-job-projects', orgId, workspaceId, jobId, projectsPager.cursor],
    enabled: tab === 'repositories' && isConfigBackup,
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/snapshots/{job_id}/projects', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! }, query: { cursor: projectsPager.cursor } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const workspaceHooks = useQuery({
    queryKey: ['backup-job-hooks', orgId, workspaceId, jobId, hooksPager.cursor],
    enabled: tab === 'repositories' && isConfigBackup,
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/snapshots/{job_id}/workspace-hooks', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! }, query: { cursor: hooksPager.cursor } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const logs = useQuery({
    queryKey: ['backup-job-logs', orgId, workspaceId, jobId],
    enabled: tab === 'log',
    queryFn: async (): Promise<LogPage> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/backups/{job_id}/logs', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! }, query: { cursor: undefined } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as LogPage
    },
  })

  // Accumulating, not paged-and-replaced (found live, 2026-09-25): the footer's "Load more" is the
  // only way forward, and it only ever appends — see this component's own doc comment for why.
  const logLines = [...(logs.data?.lines ?? []), ...morePages.flatMap((p) => p.lines)]
  const logNextCursor = morePages.length > 0 ? morePages[morePages.length - 1]!.next_cursor : logs.data?.next_cursor
  const loadMoreLog = useMutation({
    mutationFn: async (): Promise<LogPage> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/backups/{job_id}/logs', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! }, query: { cursor: logNextCursor ?? undefined } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as LogPage
    },
    onSuccess: (page) => setMorePages((prev) => [...prev, page]),
  })

  const notes = useQuery({
    queryKey: ['backup-job-notes', orgId, workspaceId, jobId],
    enabled: tab === 'notes',
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/backups/{job_id}/notes', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return (data?.items ?? []) as unknown as readonly JobNote[]
    },
  })

  const addNote = useMutation({
    mutationFn: async (body: string) => {
      const client = getApiClient()
      const { error } = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/backups/{job_id}/notes', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! } },
        body: { body },
      })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['backup-job-notes', orgId, workspaceId, jobId] }),
  })

  const exportLogs = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/backups/{job_id}/logs/export', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId! } },
        parseAs: 'blob',
      })
      if (response.error) throw new ApiProblemError(response.error as Problem)
      const blob = response.data as unknown as Blob
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `backup-${jobId}-log.csv`
      a.click()
      URL.revokeObjectURL(url)
    },
  })

  const jobProblem = job.error instanceof ApiProblemError ? job.error.problem : undefined
  const data = job.data

  return (
    <div data-scr={unitId('v-backupjob')}>
      {jobProblem && (isUnexpectedRefusal(jobProblem.refusal_kind) ? <UnexpectedRefusal problem={jobProblem} /> : <Note>{jobProblem.title}</Note>)}

      {data && (
        <>
          <div className="flex items-center gap-6 mb-6">
            <h1 className="m-0 text-section font-semibold">
              Backup <span className="font-mono">{data.job.handle}</span>
            </h1>
            <Chip variant={JOB_STATUS_CHIP[data.job.status] ?? 'plain'}>{STATUS_LABEL[data.job.status] ?? data.job.status}</Chip>
            {data.job.status === 'RUNNING' && !data.cancellation && (
              <Button data-act={undefined} ghost onClick={() => setCancelling(true)}>
                Cancel
              </Button>
            )}
            {data.cancellation && <Note>Cancelling…</Note>}
          </div>

          {data.error_category && (
            <div className="mb-6">
              <Alert variant="danger">{data.error_category}</Alert>
            </div>
          )}

          <div data-cmp="tabs" className="flex items-center gap-4 border-b border-bd mb-6">
            {(['repositories', 'details', 'notes', 'log'] as const).map((t) => (
              <button
                key={t}
                type="button"
                data-cmp="tab"
                onClick={() => {
                  pager.reset()
                  projectsPager.reset()
                  hooksPager.reset()
                  setMorePages([])
                  setTab(t)
                }}
                className={`py-3 px-4 text-body-lg font-semibold border-b-2 ${tab === t ? 'border-blue text-tx' : 'border-transparent text-muted'}`}
              >
                {t === 'repositories' ? (isConfigBackup ? 'Configuration items' : 'Repositories') : t === 'details' ? 'Details' : t === 'notes' ? 'Job notes' : 'Log'}
              </button>
            ))}
            <div data-cmp="tools" className="ml-auto pb-2">
              <IconButton
                data-act={undefined}
                icon="i-refresh"
                label="Refresh"
                onClick={() => {
                  if (tab === 'repositories' && isConfigBackup) {
                    queryClient.invalidateQueries({ queryKey: ['backup-job-projects', orgId, workspaceId, jobId] })
                    queryClient.invalidateQueries({ queryKey: ['backup-job-hooks', orgId, workspaceId, jobId] })
                  } else if (tab === 'repositories') queryClient.invalidateQueries({ queryKey: ['backup-job-repos', orgId, workspaceId, jobId] })
                  else if (tab === 'notes') queryClient.invalidateQueries({ queryKey: ['backup-job-notes', orgId, workspaceId, jobId] })
                  else if (tab === 'log') {
                    queryClient.invalidateQueries({ queryKey: ['backup-job-logs', orgId, workspaceId, jobId] })
                    setMorePages([])
                  } else queryClient.invalidateQueries({ queryKey: ['backup-job', orgId, workspaceId, jobId] })
                }}
              />
            </div>
          </div>

          {tab === 'repositories' && !isConfigBackup && (
            <div data-cmp="pane">
              <div data-cmp="panel" className="bg-panel border border-bd rounded-2">
                <div data-cmp="panel-header" className="flex items-center gap-5 p-5 border-b border-bd font-semibold text-body-lg">
                  <span className="flex-1">Repositories</span>
                  <div data-cmp="grid-tools">
                    <IconButton
                      data-act={undefined}
                      icon="i-refresh"
                      label="Refresh"
                      disabled={repositories.isFetching}
                      onClick={() => queryClient.invalidateQueries({ queryKey: ['backup-job-repos', orgId, workspaceId, jobId] })}
                    />
                  </div>
                </div>
                <GridTable
                  columns={[
                    { key: 'repository', header: 'Repository', render: (r) => r.name },
                    { key: 'status', header: 'Status', render: (r) => <Chip variant={JOB_STATUS_CHIP[r.status] ?? 'plain'}>{STATUS_LABEL[r.status] ?? r.status}</Chip> },
                    { key: 'branches', header: 'Branches', render: (r) => r.branch_count },
                    { key: 'commitsAdded', header: 'Commits added', render: (r) => r.commits_added ?? '—' },
                    { key: 'commits', header: 'Commits', render: (r) => r.commit_count },
                    { key: 'bytes', header: 'Bytes', render: (r) => formatBytes(r.content_bytes) },
                  ]}
                  rows={(repositories.data?.items ?? []) as unknown as readonly SnapshotRepository[]}
                  getRowKey={(r) => r.repository_id}
                />
                <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
                  <Note>Paged by cursor only — no total is served for this list.</Note>
                  <Pager
                    hasPrev={pager.hasPrev}
                    hasNext={Boolean(repositories.data?.next_cursor)}
                    loading={repositories.isFetching}
                    onPrev={pager.goPrev}
                    onNext={() => {
                      if (repositories.data?.next_cursor) pager.goNext(repositories.data.next_cursor)
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {tab === 'repositories' && isConfigBackup && (
            <div data-cmp="pane">
              <Note>
                A configuration backup captures repository settings, workspace objects, pipeline/deployment history and
                downloads — projects and workspace webhooks are the two kinds with a browsable list today; the rest is
                captured but not yet browsable item-by-item.{' '}
                <button type="button" data-cmp="link" className="underline text-link bg-transparent border-0 p-0 cursor-pointer" onClick={() => navigate('/backup')}>
                  See everything this job captures
                </button>
              </Note>

              <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mt-4">
                <div data-cmp="panel-header" className="flex items-center gap-5 p-5 border-b border-bd font-semibold text-body-lg">
                  <span className="flex-1">Projects</span>
                  <div data-cmp="grid-tools">
                    <IconButton
                      data-act={undefined}
                      icon="i-refresh"
                      label="Refresh"
                      disabled={projects.isFetching}
                      onClick={() => queryClient.invalidateQueries({ queryKey: ['backup-job-projects', orgId, workspaceId, jobId] })}
                    />
                  </div>
                </div>
                <GridTable
                  columns={[
                    { key: 'key', header: 'Project key', render: (r) => r.project_key },
                    { key: 'name', header: 'Name', render: (r) => r.name },
                    { key: 'repos', header: 'Repositories', render: (r) => r.repository_count },
                    { key: 'captured', header: 'Captured', render: (r) => (r.captured_at ? formatJobTime(r.captured_at) : '—') },
                  ]}
                  rows={(projects.data?.items ?? []) as unknown as readonly SnapshotProject[]}
                  getRowKey={(r) => r.project_key}
                />
                <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
                  <Note>Paged by cursor only — no total is served for this list.</Note>
                  <Pager
                    hasPrev={projectsPager.hasPrev}
                    hasNext={Boolean(projects.data?.next_cursor)}
                    loading={projects.isFetching}
                    onPrev={projectsPager.goPrev}
                    onNext={() => {
                      if (projects.data?.next_cursor) projectsPager.goNext(projects.data.next_cursor)
                    }}
                  />
                </div>
              </div>

              <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mt-4">
                <div data-cmp="panel-header" className="flex items-center gap-5 p-5 border-b border-bd font-semibold text-body-lg">
                  <span className="flex-1">Workspace webhooks</span>
                  <div data-cmp="grid-tools">
                    <IconButton
                      data-act={undefined}
                      icon="i-refresh"
                      label="Refresh"
                      disabled={workspaceHooks.isFetching}
                      onClick={() => queryClient.invalidateQueries({ queryKey: ['backup-job-hooks', orgId, workspaceId, jobId] })}
                    />
                  </div>
                </div>
                <GridTable
                  columns={[
                    { key: 'url', header: 'URL', render: (item) => item.row.url },
                    { key: 'events', header: 'Events', render: (item) => item.row.events.join(', ') },
                    { key: 'secret', header: 'Had a secret', render: (item) => (item.row.had_secret ? 'Yes' : 'No') },
                  ]}
                  rows={((workspaceHooks.data?.items ?? []) as unknown as readonly SnapshotWorkspaceHook[]).map((row, i) => ({ row, i }))}
                  getRowKey={(item) => String(item.i)}
                />
                <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
                  <Note>Paged by cursor only — no total is served for this list.</Note>
                  <Pager
                    hasPrev={hooksPager.hasPrev}
                    hasNext={Boolean(workspaceHooks.data?.next_cursor)}
                    loading={workspaceHooks.isFetching}
                    onPrev={hooksPager.goPrev}
                    onNext={() => {
                      if (workspaceHooks.data?.next_cursor) hooksPager.goNext(workspaceHooks.data.next_cursor)
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {tab === 'details' && (
            <div data-cmp="pane">
              <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6">
                <div data-cmp="panel-header" className="mb-4 font-semibold text-body-lg">
                  Job details
                </div>
                <div data-cmp="meta-block" className="grid gap-4" style={{ gridTemplateColumns: '200px 1fr' }}>
                  <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                    Job ID
                  </div>
                  <div data-cmp="kv-value" className="font-mono text-body-lg">{data.job.job_id}</div>
                  <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                    Kind
                  </div>
                  <div data-cmp="kv-value" className="text-body-lg">{data.backup_kind === 'CONFIG_BACKUP' ? 'Configuration backup' : 'Data backup'}</div>
                  <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                    Initiator
                  </div>
                  <div data-cmp="kv-value" className="text-body-lg">{data.triggered_by ? data.triggered_by.display_name : 'System'}</div>
                  <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                    Start time
                  </div>
                  <div data-cmp="kv-value" className="text-body-lg">{data.started_at ? data.started_at.display : '—'}</div>
                  <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                    End time
                  </div>
                  <div data-cmp="kv-value" className="text-body-lg">{data.completed_at ? data.completed_at.display : '—'}</div>
                  <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                    Time taken
                  </div>
                  <div data-cmp="kv-value" className="text-body-lg">{data.duration ? data.duration.display : '—'}</div>
                  <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                    Repositories
                  </div>
                  <div data-cmp="kv-value" className="text-body-lg">{data.repositories.caption}</div>
                  <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                    Bytes added
                  </div>
                  <div data-cmp="kv-value" className="text-body-lg">{data.bytes_added ? data.bytes_added.display : '—'}</div>
                  <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
                    Retention
                  </div>
                  <div data-cmp="kv-value" className="text-body-lg">
                    {formatJobTime(data.retention.retention_until)} {data.retention.expired && <Chip variant="off">expired</Chip>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === 'notes' && (
            <div data-cmp="pane">
              <NoteThread notes={notes.data ?? []} pending={addNote.isPending} onAdd={(body) => addNote.mutate(body)} addActionId={actionId('job.note.add')} />
            </div>
          )}

          {tab === 'log' && (
            <div data-cmp="pane">
              <div data-cmp="panel" className="bg-panel border border-bd rounded-2">
                <div data-cmp="panel-header" className="flex items-center gap-5 p-5 border-b border-bd font-semibold text-body-lg">
                  <span className="flex-1">Log</span>
                  <div data-cmp="grid-tools" className="flex items-center gap-4">
                    <IconButton
                      data-act={undefined}
                      icon="i-refresh"
                      label="Refresh"
                      disabled={logs.isFetching}
                      onClick={() => {
                        queryClient.invalidateQueries({ queryKey: ['backup-job-logs', orgId, workspaceId, jobId] })
                        setMorePages([])
                      }}
                    />
                    <Button data-act={actionId('backupLogs.export')} ghost disabled={logs.data?.retention_expired || exportLogs.isPending} onClick={() => exportLogs.mutate()} title={logs.data?.retention_expired ? 'Logs expired' : undefined}>
                      Export logs
                    </Button>
                  </div>
                </div>
                {logs.data?.retention_expired ? (
                  <Note>Logs expired. The log group's retention boundary has passed for this job.</Note>
                ) : (
                  <>
                    <div className="font-mono text-body flex flex-col gap-2 p-5">
                      {logLines.map((line, i) => (
                        <div key={i} className={line.level === 'ERROR' ? 'text-fail' : line.level === 'WARNING' ? 'text-warn' : 'text-tx2'}>
                          [{line.level}] {line.at} — {line.message}
                        </div>
                      ))}
                      {logLines.length === 0 && !logs.isFetching && <Note>This job has not logged anything yet.</Note>}
                    </div>
                    <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
                      <Note>
                        {logLines.length} line{logLines.length === 1 ? '' : 's'} loaded so far — logs only ever load forward, so
                        nothing already shown here goes away.
                      </Note>
                      {logNextCursor && (
                        <Button data-act={undefined} ghost disabled={loadMoreLog.isPending} onClick={() => loadMoreLog.mutate()}>
                          {loadMoreLog.isPending ? 'Loading…' : 'Load more'}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {cancelling && (
            <MCancel
              orgId={orgId}
              workspaceId={workspaceId}
              job={data.job}
              progress={data.repositories}
              onDismiss={() => setCancelling(false)}
              onCancelled={() => {
                setCancelling(false)
                queryClient.invalidateQueries({ queryKey: ['backup-job', orgId, workspaceId, jobId] })
              }}
            />
          )}
        </>
      )}
    </div>
  )
}
